import React, { useState } from "react";
import Layout from "../form-layout";
import { SIDE_EFFECTS, SIDE_EFFECT_SEVERITY } from "../../data/eligibility-rules";

const RESOLVED_STATUS_OPTIONS = ["Currently experiencing", "Resolved"];

// The symptom + severity (+ optional current/resolved status) module.
//
// Two callers:
//  - glp1-side-effects.jsx (kept Q17 pathway): the doc presents this as a single
//    question with "None of the below" as one of the checkboxes, no status field —
//    includeNoneOption stays true (default), showResolvedStatus stays false.
//  - current-side-effects-module.jsx (Form B, after the Q5 yes/no gate): the doc's
//    module is "symptom + severity + current/resolved status", reached only after
//    answering "Yes" to a separate yes/no question, so "None" doesn't belong here —
//    includeNoneOption={false}, showResolvedStatus={true}.
export default function SideEffects({
  formData,
  setFormData,
  onBack,
  goToStep,
  nextStepId,
  includeNoneOption = true,
  showResolvedStatus = false,
  title = "Are you experiencing any side effects with your medication?",
}) {
  const [selected, setSelected] = useState(formData.currentSideEffects || []);
  const [severity, setSeverity] = useState(formData.sideEffectSeverity || null);
  const [status, setStatus] = useState(formData.sideEffectStatus || "");
  const [details, setDetails] = useState(formData.sideEffectDetails || "");

  const toggle = (symptom) => {
    setSelected((prev) => {
      if (symptom === "None of the below") return prev.includes(symptom) ? [] : [symptom];
      const withoutNone = prev.filter((s) => s !== "None of the below");
      return withoutNone.includes(symptom)
        ? withoutNone.filter((s) => s !== symptom)
        : [...withoutNone, symptom];
    });
  };

  const hasSymptoms = selected.length > 0 && !selected.includes("None of the below");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selected.length === 0) return;
    if (hasSymptoms && !severity) return;
    if (hasSymptoms && showResolvedStatus && !status) return;

    setFormData((prev) => ({
      ...prev,
      currentSideEffects: selected,
      sideEffectSeverity: hasSymptoms ? severity : null,
      sideEffectStatus: hasSymptoms && showResolvedStatus ? status : "",
      sideEffectDetails: details,
    }));
    localStorage.setItem("currentSideEffects", JSON.stringify(selected));
    localStorage.setItem("sideEffectSeverity", hasSymptoms ? String(severity) : "");
    localStorage.setItem("sideEffectStatus", hasSymptoms && showResolvedStatus ? status : "");
    localStorage.setItem("sideEffectDetails", details);

    goToStep(nextStepId);
  };

  const symptomOptions = includeNoneOption ? ["None of the below", ...SIDE_EFFECTS] : SIDE_EFFECTS;
  const isBlocked =
    selected.length === 0 || (hasSymptoms && !severity) || (hasSymptoms && showResolvedStatus && !status);

  return (
    <Layout goToStep={goToStep} onBack={onBack}>
      <h2 className="text-sm sm:text-[14px] font-medium text-[#000000] mb-6">{title}</h2>

      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {symptomOptions.map((symptom) => (
          <label key={symptom} className="flex items-center gap-2 p-3 rounded-lg cursor-pointer border transition-all text-sm">
            <input
              type="checkbox"
              checked={selected.includes(symptom)}
              onChange={() => toggle(symptom)}
              className="h-4 w-4 accent-sky-400 cursor-pointer"
            />
            <span>{symptom}</span>
          </label>
        ))}

        {hasSymptoms && (
          <div className="ml-2 flex flex-col gap-2 mt-2">
            <p className="text-gray-700 text-sm font-medium">How severe was your side effect?</p>
            {SIDE_EFFECT_SEVERITY.map((level) => (
              <label key={level.value} className="flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm">
                <input
                  type="radio"
                  name="sideEffectSeverity"
                  checked={severity === level.value}
                  onChange={() => setSeverity(level.value)}
                  className="h-4 w-4 accent-sky-400 cursor-pointer"
                />
                <span>{level.label}</span>
              </label>
            ))}

            {showResolvedStatus && (
              <>
                <p className="text-gray-700 text-sm font-medium mt-2">Is this currently ongoing, or has it resolved?</p>
                {RESOLVED_STATUS_OPTIONS.map((option) => (
                  <label key={option} className="flex items-center gap-2 p-2 rounded-lg border cursor-pointer text-sm">
                    <input
                      type="radio"
                      name="sideEffectStatus"
                      checked={status === option}
                      onChange={() => setStatus(option)}
                      className="h-4 w-4 accent-sky-400 cursor-pointer"
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </>
            )}

            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Share more details about your symptoms (optional)"
              className="border border-gray-300 rounded-lg p-3 text-sm w-full min-h-[70px]"
            />
          </div>
        )}

        <div className="flex justify-start mt-2">
          <button
            type="submit"
            disabled={isBlocked}
            className={`w-[210px] text-sm font-semibold rounded-lg py-3 transition-colors
              ${isBlocked ? "bg-gray-300 text-gray-600 cursor-not-allowed" : "bg-[#35C3D9] text-white hover:bg-[#27AAC0]"}`}
          >
            Next →
          </button>
        </div>
      </form>
    </Layout>
  );
}
